import React__default from 'react';
import { useLocalization } from '../../Provider/Provider.js';
import { useTable as useTable$1, useRowState, useSortBy, useExpanded, usePagination } from 'react-table';
import { getColumnsFromChildren, getInternalSortRules, sanitizeRowProps } from '../util.js';
import { sortTypes } from '../util/sortTypes.js';
import { useRowEditing } from './plugins/useRowEditing.js';
import { useRowActions } from './plugins/useRowActions.js';
import { useRowSelect } from './plugins/useRowSelect.js';
import { useTableKeyboardNavigation } from './useTableKeyboardNavigation.js';
import { useRowDraggable } from './plugins/useRowDraggable.js';

const useTableRowActive = (activeIndex, rows, rowExpansionRenderer, handleonRowActive) => {
  React__default.useEffect(() => {
    if (activeIndex !== undefined && rows.length && handleonRowActive) {
      const focusedRow = rows[activeIndex];

      if (focusedRow) {
        const sanitizedFocusedRow = sanitizeRowProps(focusedRow, rowExpansionRenderer);
        handleonRowActive(sanitizedFocusedRow);
      }
    }
  }, [activeIndex, rows]);
};

const useTableInstance = (instance, ref) => {
  const sanitizedInstance = React__default.useMemo(() => ({
    toggleAllRowsExpanded: instance.toggleAllRowsExpanded,
    toggleHideAllColumns: instance.toggleHideAllColumns,
    toggleHideColumn: instance.toggleHideColumn,
    toggleEditing: instance.toggleEditing,
    toggleRowEditing: instance.toggleRowEditing,
    resetRowEditing: instance.resetRowEditing,
    toggleRowExpanded: instance.toggleRowExpanded,
    toggleSortBy: instance.toggleSortBy
  }), []);
  React__default.useEffect(() => {
    if (ref !== null && ref !== void 0 && ref.current) {
      ref.current.instance = sanitizedInstance;
    }
  }, [ref]);
  return sanitizedInstance;
};

const useTablePaginationListener = (disablePagination, onPaginate, state) => {
  React__default.useEffect(() => {
    if (!disablePagination && onPaginate) {
      onPaginate(state.pageIndex, state.pageSize);
    }
  }, [state.pageIndex, state.pageSize]);
};

const useTableSortingListener = (data, sortedRows, onSort, manualSorting, state) => {
  React__default.useEffect(() => {
    if (onSort) {
      const sortRules = state.sortBy.map(rule => ({
        accessor: rule.id,
        desc: rule.desc
      }));

      if (manualSorting) {
        onSort(sortRules);
      } else {
        let sortedData;

        if (sortRules.length && sortedRows !== null && sortedRows !== void 0 && sortedRows.length) {
          sortedData = sortedRows.map(row => row.original);
        }

        onSort(sortRules, sortedData || data);
      }
    }
  }, [onSort && JSON.stringify(state.sortBy), manualSorting]);
};

const DEFAULT_PAGE_SIZE = 10;
const useTable = (props, ref) => {
  var _otherProps$tabIndex;

  const {
    children,
    data,
    dangerouslyHijackGlobalKeyboardNavigation: _,
    onRowClick,
    onRowDrag,
    onSelectedRows,
    rowClassName,
    rowExpansionRenderer,
    rowHeight,
    selectedRows,
    // sorting
    disableSorting,
    manualSorting,
    onSort,
    sortRules,
    //index
    activeIndex: _1,
    defaultActiveIndex: _2,
    onChangeActiveIndex: _3,
    // pagination
    disablePagination = true,
    length,
    onPaginate,
    pageSize = DEFAULT_PAGE_SIZE,
    pageIndex = 0,
    // row editing
    inlineEditingUniqueId = undefined,
    onRowCreate,
    // actions
    actions,
    onRowEdit,
    onRowCopy,
    onRowDelete,
    onRowActive,
    windowed = false,
    ...otherProps
  } = props;

  if (onSelectedRows && !selectedRows || !onSelectedRows && selectedRows) {
    throw new Error('Selected rows in a Table component are fully controlled - you must pass both the `onSelectedRows` and `selectedRows` props when using row selection');
  }

  const {
    texts
  } = useLocalization();
  const {
    columns,
    sortRules: defaultSortRules
  } = React__default.useMemo(() => getColumnsFromChildren(children, rowExpansionRenderer), [children, rowExpansionRenderer]);
  const manualPagination = !disablePagination && !!onPaginate && !!length;
  const {
    headerGroups,
    rows,
    sortedRows,
    prepareRow: prepareBaseRow,
    state,
    // pagination
    page,
    gotoPage,
    setPageSize,
    ...instance
  } = useTable$1({
    columns,
    data,
    initialState: {
      // eslint-disable-next-line
      // @ts-ignore: not sure how to type this correctly right now
      sortBy: getInternalSortRules(sortRules) || defaultSortRules,
      pageSize: !disablePagination ? pageSize : undefined,
      pageIndex: !disablePagination ? pageIndex : undefined
    },
    manualPagination,
    pageCount: manualPagination && length ? Math.ceil(length / pageSize) : -1,
    manualSortBy: manualSorting,
    disableSortBy: disableSorting,
    // most of these resets preventions are needed for editing mode
    autoResetExpanded: false,
    autoResetSelectedRows: false,
    autoResetSortBy: false,
    autoResetPage: false,
    sortTypes: React__default.useMemo(() => sortTypes, []),
    useControlledState: currentState => {
      return React__default.useMemo(() => ({ ...currentState,
        selectedRowIds: selectedRows || []
      }), [currentState, selectedRows]);
    }
  }, useRowState, useSortBy, useExpanded, usePagination, useRowSelect(onSelectedRows), useRowDraggable(onRowDrag), useRowEditing(inlineEditingUniqueId), useRowActions(inlineEditingUniqueId, {
    onRowCreate,
    onRowEdit,
    onRowCopy,
    onRowDelete
  }, actions, rowExpansionRenderer, texts, windowed));
  useTablePaginationListener(disablePagination, onPaginate, state);
  useTableSortingListener(data, sortedRows, onSort, !!manualSorting, state);
  const sanitizedInstance = useTableInstance(instance, ref);
  const visibleRows = !disablePagination && !manualPagination ? page : rows;
  const [activeIndex, setActiveIndex, handleKeyDown, handleFocus] = useTableKeyboardNavigation(props, visibleRows, {
    onRowClick,
    onRowCreate,
    onRowEdit,
    onRowCopy,
    onRowDelete,
    rowExpansionRenderer
  }, ref);
  useTableRowActive(activeIndex, rows, rowExpansionRenderer, onRowActive);
  const prepareRow = React__default.useCallback((row, index) => {
    prepareBaseRow(row);

    row.setActive = () => setActiveIndex(index);
  }, [prepareBaseRow, setActiveIndex]);
  return {
    rowProps: {
      activeIndex,
      setActiveIndex,
      onRowClick,
      rowClassName,
      rowExpansionRenderer,
      rowHeight,
      inlineEditingUniqueId
    },
    tableProps: { ...otherProps,
      headerGroups,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      tabIndex: (_otherProps$tabIndex = otherProps.tabIndex) !== null && _otherProps$tabIndex !== void 0 ? _otherProps$tabIndex : 0
    },
    state,
    pagination: !disablePagination ? {
      length: manualPagination && length ? length : data.length,
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
      setPageIndex: gotoPage,
      setPageSize: setPageSize
    } : null,
    rows: visibleRows,
    prepareRow,
    instance: sanitizedInstance
  };
};

export { useTable };
//# sourceMappingURL=useTable.js.map
